import * as Vue from "vue";

import {
  IProgrammingGanttRow,
  IProgrammingGanttSchedule,
  ProgrammingGantt,
} from "@/components/programming-gantt/programming-gantt";
import { ProgrammingGanttRow } from "@/components/programming-gantt/programming-gantt-row";
import { ProgrammingGanttRowScheduleContextMenu } from "@/components/programming-gantt/programming-gantt-row-schedule-context-menu";
import { $, createSVG } from "@/utils/selectors";

export class ProgrammingGanttRowSchedule<ROW extends IProgrammingGanttRow, SCHEDULE extends IProgrammingGanttSchedule> {
  private static readonly VERTICAL_SPACING = 10;
  private static readonly HORIZONTAL_SPACING = 1;
  private static readonly MIN_WIDTH = 1;

  private $schedule!: SVGElement | null;
  private $contentContainer!: HTMLElement;

  private contextMenu!: ProgrammingGanttRowScheduleContextMenu<ROW, SCHEDULE>;

  private readonly x: number;
  private readonly y: number;
  private readonly height: number;
  private readonly width: number;

  private isVisible: boolean = true;

  constructor(
    private readonly gantt: ProgrammingGantt<ROW, SCHEDULE>,
    private readonly row: ProgrammingGanttRow<ROW, SCHEDULE>,
    private readonly schedule: SCHEDULE,
  ) {
    const endDateXPosition = gantt.getDateXPosition(schedule.end);

    this.x = gantt.getDateXPosition(schedule.start) + ProgrammingGanttRowSchedule.HORIZONTAL_SPACING;
    this.y = this.row.y() + ProgrammingGanttRowSchedule.VERTICAL_SPACING;
    this.height = gantt.getRowHeight() - ProgrammingGanttRowSchedule.VERTICAL_SPACING * 2;
    this.width = Math.max(
      ProgrammingGanttRowSchedule.MIN_WIDTH,
      endDateXPosition - this.x - ProgrammingGanttRowSchedule.HORIZONTAL_SPACING * 2,
    );
  }

  public handleVisibility() {
    if (this.isInViewport()) {
      this.mount();
      return;
    }

    this.unmount();
  }

  private mount() {
    if (this.$schedule) {
      return;
    }

    this.$schedule = createSVG("foreignObject", {
      append_to: this.row.group(),
      x: this.x,
      width: this.width,
      height: this.height,
      y: this.y,
    });

    $.attr(this.$schedule, "start-date", this.formatDate(this.schedule.start));
    $.attr(this.$schedule, "end-date", this.formatDate(this.schedule.end));

    this.$contentContainer = document.createElement("div");
    this.$contentContainer.classList.add("gantt-row__schedule");
    this.$contentContainer.classList.add(this.gantt.getOptions().getBarBackground(this.schedule, this.row.getRow()));
    const content = this.gantt.getOptions().getBarContent(this.schedule, this.row.getRow());
    if (content?.type === "icon") {
      if (typeof content?.icon === "string") {
        const iconElement = document.createElement("i");
        iconElement.classList.add("pi", content.icon);
        this.$contentContainer.appendChild(iconElement);
      } else {
        const iconElement = document.createElement("div");
        const app = Vue.createApp(content.icon);
        app.mount(iconElement);
        this.$contentContainer.appendChild(iconElement);
      }
    } else if (content?.type === "text") {
      const textElement = document.createElement("span");
      textElement.innerHTML = content.label;
      this.$contentContainer.appendChild(textElement);
    }

    this.$schedule.appendChild(this.$contentContainer);
    this.updateRender();

    this.contextMenu = new ProgrammingGanttRowScheduleContextMenu(this.gantt, this.row, this);
    this.$schedule.addEventListener("click", this.onContextMenu);

    this.addEventListeners();
  }

  public getRawSchedule() {
    return this.schedule;
  }

  private onContextMenu = (event: MouseEvent) => {
    this.contextMenu.toggleMenuOn(event);
  };

  private formatDate(date: Date) {
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  }

  private unmount() {
    if (!this.$schedule) {
      return;
    }

    this.contextMenu.destroy();
    this.$schedule.removeEventListener("contextmenu", this.onContextMenu);
    this.$schedule.remove();
    this.$schedule = null;
  }

  public remove() {
    this.unmount();
  }

  private isInViewport() {
    const viewport = this.gantt.getVisibleViewport();
    const lineTop = this.getY();
    const lineBottom = this.getBottom();
    const lineX = this.getX();
    const lineRight = this.getRight();

    const isVerticallyVisible =
      (lineTop >= viewport.y && lineTop <= viewport.bottom) || // La partie supérieure de la ligne est visible
      (lineBottom >= viewport.y && lineBottom <= viewport.bottom) || // La partie inférieure de la ligne est visible
      (lineTop <= viewport.y && lineBottom >= viewport.bottom); // La ligne couvre complètement le viewport

    const isHorizontallyVisible =
      (lineX >= viewport.x && lineX <= viewport.right) || // La partie gauche de la ligne est visible
      (lineRight >= viewport.x && lineRight <= viewport.right) || // La partie droite de la ligne est visible
      (lineX <= viewport.x && lineRight >= viewport.right); // La ligne couvre complètement le viewport horizontalement

    return isVerticallyVisible && isHorizontallyVisible;
  }

  private addEventListeners() {
    this.$contentContainer?.addEventListener("mouseenter", () => {
      this.$contentContainer?.classList.add("highlight");
    });

    this.$contentContainer?.addEventListener("mouseleave", () => {
      this.$contentContainer?.classList.remove("highlight");
    });
  }

  private getX() {
    return this.x;
  }

  private getY() {
    return this.y;
  }

  private getHeight() {
    return this.height;
  }

  private getBottom() {
    return this.getY() + this.getHeight();
  }

  private getWidth() {
    return this.width;
  }

  private getRight() {
    return this.getX() + this.getWidth();
  }

  public hide() {
    this.isVisible = false;
    this.updateRender();
  }

  public show() {
    this.isVisible = true;
    this.updateRender();
  }

  public updateRender() {
    this.$contentContainer?.setAttribute("visible", `${this.isVisible}`);
  }
}
